import React, { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Html5Qrcode } from "html5-qrcode";
import { ScanLine, Loader2, Camera, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export function extractCode(raw) {
  const c = String(raw || "").trim();
  if (c.includes("/s/")) {
    const seg = c.split("/s/")[1].split("?")[0].split("#")[0].replace(/\/+$/, "");
    try { return decodeURIComponent(seg); } catch { return seg; }
  }
  return c;
}

export function QRLabel({ value, caption, size = 96 }) {
  if (!value) return null;
  const link = typeof window !== "undefined"
    ? `${window.location.origin}/s/${encodeURIComponent(String(value))}`
    : String(value);
  return (
    <div className="inline-flex flex-col items-center gap-1 bg-white border border-black/10 rounded-xl p-3" data-testid={`qr-${value}`}>
      <QRCodeSVG value={link} size={size} fgColor="#0D530E" level="M" />
      <span className="text-[11px] font-mono text-[#0D530E] tracking-tight text-center">{value}</span>
      {caption && <span className="text-[10px] text-[#5C5C5C]">{caption}</span>}
    </div>
  );
}

export function CameraScanner({ onDetect, onClose }) {
  const [err, setErr] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    const el = "qr-reader-cam";
    const scanner = new Html5Qrcode(el);
    ref.current = scanner;
    let stopped = false;
    scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: 240 },
      (text) => { if (!stopped) { stopped = true; scanner.stop().catch(() => {}).finally(() => onDetect(text)); } },
      () => {}
    ).catch(() => setErr("Camera unavailable — check permissions or use the type-in field."));
    return () => { try { scanner.stop().catch(() => {}); } catch (e) {} };
  }, [onDetect]);
  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4" data-testid="camera-scanner">
      <div className="bg-white rounded-2xl p-4 w-full max-w-sm">
        <div className="flex justify-between items-center mb-3">
          <p className="font-display text-lg text-[#0D530E] flex items-center gap-2"><Camera size={18} /> Scan a QR code</p>
          <button onClick={onClose} data-testid="camera-close"><X size={20} /></button>
        </div>
        <div id="qr-reader-cam" className="rounded-xl overflow-hidden bg-black" />
        {err && <p className="text-[#B23B3B] text-sm mt-3">{err}</p>}
        <p className="text-xs text-[#5C5C5C] mt-3">Point the camera at an order, package or item QR label.</p>
      </div>
    </div>
  );
}

// Scanner input. mode: resolve (default) calls /admin/resolve; if onCode is given it returns the raw code.
export function ScanInput({ onResolve, onCode, placeholder = "Scan or type a code…" }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cam, setCam] = useState(false);

  const handle = async (raw) => {
    const c = extractCode(raw);
    if (!c) return;
    if (onCode) { onCode(c); setCode(""); return; }
    setBusy(true);
    try { const { data } = await api.get(`/admin/resolve/${encodeURIComponent(c)}`); onResolve?.(data); setCode(""); }
    catch { toast.error(`"${c}" not recognised`); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div className="flex items-center gap-2 bg-white border border-[#306D29]/30 rounded-full pl-4 pr-2 py-1.5" data-testid="scan-input">
        <ScanLine size={18} className="text-[#306D29] shrink-0" />
        <input value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handle(code)}
          placeholder={placeholder} className="flex-1 bg-transparent text-sm focus:outline-none" data-testid="scan-field" />
        <button onClick={() => setCam(true)} title="Scan with camera" data-testid="scan-camera-btn" className="shrink-0 text-[#306D29] p-1.5"><Camera size={18} /></button>
        <button onClick={() => handle(code)} disabled={busy} className="shrink-0 bg-[#306D29] text-white text-sm font-semibold px-4 py-1.5 rounded-full disabled:opacity-60">
          {busy ? <Loader2 size={14} className="animate-spin" /> : "Go"}
        </button>
      </div>
      {cam && <CameraScanner onDetect={(t) => { setCam(false); handle(t); }} onClose={() => setCam(false)} />}
    </>
  );
}
